/**
 * "Watch" a shared project from the kanban's live bar: be told when someone starts editing in it. Watched projects are
 * remembered per browser; the sidebar (always on screen) compares each `/api/v1/live` answer with the previous one and
 * says who started: a person typing, or an AI agent writing.
 */
const KEY = 'flow.watch.projects';

export function watchedProjects() {
  try {
    const ids = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function setWatched(projectId, on) {
  const ids = new Set(watchedProjects());
  if (on) { ids.add(projectId); } else { ids.delete(projectId); }
  try { localStorage.setItem(KEY, JSON.stringify([...ids])); } catch { /* not remembered */ }
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent('flow:watchChanged', { detail: { projectId, on } })); }
  return [...ids];
}

const editors = (project) => {
  const out = new Map();
  (project?.people || []).forEach((person) => {
    if ((person.views || []).some((view) => view.typing)) { out.set(`p:${person.id}`, String(person.name || 'Someone').split(/\s+/)[0]); }
  });
  (project?.agents || []).forEach((agent) => {
    if ((agent.paths || []).length > 0 && !agent.paused) {
      const driver = agent.driver?.name ? ` for ${String(agent.driver.name).split(/\s+/)[0]}` : '';
      out.set(`a:${agent.id}`, `${agent.label || 'An AI agent'}${driver}`);
    }
  });
  return out;
};

/** Who started editing in a watched project between two answers: `[{ projectId, name, who: [labels] }]`. */
export function startedEditing(previous, next, watched) {
  if (!next) { return []; }
  const before = new Map((previous?.projects || []).map((project) => [project.projectId, editors(project)]));
  return (next.projects || [])
    .filter((project) => watched.includes(project.projectId))
    .map((project) => {
      const was = before.get(project.projectId) || new Map();
      const who = [...editors(project).entries()].filter(([key]) => !was.has(key)).map(([, label]) => label);
      return { projectId: project.projectId, name: project.name || project.projectId, who };
    })
    .filter((entry) => entry.who.length > 0);
}

/** "store-locator: Luca started editing", "…: Luca and Flow chat for Luca started editing". */
export function startedText(entry) {
  const who = entry.who.length <= 2 ? entry.who.join(' and ') : `${entry.who.slice(0, 2).join(', ')} and ${entry.who.length - 2} more`;
  return `${who} started editing ${entry.name}`;
}

/** The live bar's line for a project: "Luca typing · Flow chat for Luca writing · Sara on the preview". */
export function liveBarParts(project, you = null) {
  const parts = [];
  (project?.people || []).forEach((person) => {
    const name = String(person.name || 'Someone').split(/\s+/)[0];
    const views = person.views || [];
    const latest = [...views].sort((a, b) => (b.at || 0) - (a.at || 0))[0];
    if (views.some((view) => view.typing)) { parts.push(`${name} typing`); } else if (latest?.kind === 'preview') { parts.push(`${name} on the preview`); } else { parts.push(`${name} here`); }
  });
  (project?.agents || []).forEach((agent) => {
    const mine = you?.id && agent.driver?.id === you.id;
    const driver = agent.driver?.name ? ` for ${mine ? 'you' : String(agent.driver.name).split(/\s+/)[0]}` : '';
    parts.push(`${agent.label || 'AI agent'}${driver} ${agent.paused ? 'paused' : 'writing'}`);
  });
  return parts;
}
